/**
 * Season Winnings extension hooks.
 *
 * Two surfaces over one computation (see the backend's SeasonWinningsService):
 *   - /admin-ops/organizations/:orgId/season/:year/* — the association's
 *     season board, for admins and delegates.
 *   - /dashboard-data/my/season-winnings — the contestant's own season.
 *   - /dashboard-data/season-standings — the browse-anywhere standings board
 *     (any association, totals only) for every signed-in user.
 *
 * A season total is COMPUTED managed winnings (prize money from rodeos
 * HighCall runs, derived from live standings) plus ADDITIONAL winnings: the
 * itemised external results a delegate enters for rodeos it doesn't run
 * (one row per event — amount, type, rodeo, date, placement), plus a legacy
 * lump amount on the adjustment row that counts until a delegate clears it.
 * The roster is expensive to resolve server-side, so these queries are cached
 * longer than the app default.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Types ---

/**
 * One result on a contestant's season board — at a rodeo HighCall ran, or
 * (`external: true`) one it did not, as a delegate recorded it.
 */
export interface SeasonBreakdownRow {
  /** Null on an external row: HighCall has no rodeo for it. */
  rodeo_id: string | null;
  /** For an external row, the rodeo name the delegate typed. */
  rodeo_name: string | null;
  /** For an external row, its event date. */
  start_date: string | null;
  /** Null on an external row. */
  event_id: string | null;
  event_name: string;
  /**
   * `'average'` = the event's overall placing; `'round'` = a go-round payout;
   * `'external'` = a delegate-entered result from a rodeo HighCall did not run.
   */
  scope: 'average' | 'round' | 'external';
  round_number: number | null;
  placement: number | null;
  /** THIS contestant's share — half of a team roping payout; the entered amount when external. */
  prize_amount: number | null;
  /** The undivided entry total when the prize was shared; null for solo and external. */
  team_prize: number | null;
  external: boolean;
  /** The external result behind an external row; null otherwise. */
  external_result_id: string | null;
}

/** One delegate-entered result from a rodeo HighCall did not run. */
export interface SeasonExternalResult {
  id: string;
  organization_id: string;
  user_profile_id: string;
  season_year: number;
  rodeo_name: string;
  /** YYYY-MM-DD */
  event_date: string;
  /** A season board key, e.g. 'Steer Wrestling' or 'Team Roping — Header'. */
  event_type_key: string;
  placement: number | null;
  amount: number;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** What the entry form sends. `placement: null` = no place recorded. */
export interface ExternalResultInput {
  amount: number;
  event_type_key: string;
  rodeo_name: string;
  event_date: string;
  placement?: number | null;
  notes?: string | null;
}

export interface SeasonRosterRow {
  user_profile_id: string;
  /** The profile's opt-in display name where set, else the legal name. */
  display_name: string | null;
  /** The legal name — delegate surfaces only; never on the public board. */
  full_name?: string | null;
  member_number: string | null;
  is_member: boolean;
  /** Won at rodeos HighCall manages. Computed, never stored. */
  managed_winnings: number;
  /** Itemised external results + the legacy lump amount. */
  additional_winnings: number;
  /** The legacy lump on the adjustment row, still counting until cleared. Delegate surfaces only. */
  legacy_additional_winnings?: number;
  total_winnings: number;
  finals_eligible: boolean;
  notes: string | null;
  events_entered: number;
  events_placed: number;
  rodeos_attended: number;
  /** Empty on the roster; populated on the single-contestant read. */
  breakdown: SeasonBreakdownRow[];
}

export interface SeasonRosterResponse {
  organization_id: string;
  season_year: number;
  /** Rodeos the association ran this season — the denominator for attendance. */
  rodeo_count: number;
  contestants: SeasonRosterRow[];
}

export interface SeasonAdjustment {
  /** LEGACY lump amount — new outside winnings are external results. */
  additional_winnings: number;
  finals_eligible: boolean;
  notes: string | null;
  updated_at: string | null;
}

export interface ContestantSeasonResponse {
  organization_id: string;
  season_year: number;
  rodeo_count: number;
  contestant: SeasonRosterRow;
  adjustment: SeasonAdjustment;
  /** Newest event first. */
  external_results: SeasonExternalResult[];
  /** The legacy lump (same as `adjustment.additional_winnings`), shown for re-entry and clearing. */
  legacy_additional_winnings: number;
  /** The event types an external result may be filed under, in dropdown order. */
  allowed_event_type_keys: string[];
}

/**
 * Patch shape for the adjustment. Omitting a field leaves it alone; sending
 * `0` or `false` sets it. Never coerce these through a falsy check.
 */
export interface UpsertSeasonAdjustmentInput {
  additional_winnings?: number;
  finals_eligible?: boolean;
  notes?: string | null;
}

/**
 * One association the contestant may ask for a season at. NOT the same list as
 * their memberships: someone can win money at an association's rodeo without
 * ever joining it, and that money is still theirs to see.
 */
export interface SeasonOrganization {
  organization_id: string;
  organization_name: string | null;
  short_name: string | null;
  /** False = they competed here without ever joining. */
  is_member: boolean;
  /** Seasons with something to show, newest first. Never empty. */
  years: number[];
}

export interface MySeasonWinningsResponse {
  organization_id: string;
  season_year: number;
  rodeo_count: number;
  managed_winnings: number;
  additional_winnings: number;
  total_winnings: number;
  finals_eligible: boolean;
  events_entered: number;
  events_placed: number;
  rodeos_attended: number;
  breakdown: SeasonBreakdownRow[];
  /** Years with something to show — drives the year picker. */
  available_years: number[];
}

/**
 * One row on the association standings board. The roster row minus the
 * delegate-only fields (no notes, no breakdown, no legal name), plus the
 * competition place under the applied filter.
 */
export interface SeasonStandingsRow
  extends Omit<SeasonRosterRow, 'notes' | 'breakdown' | 'full_name' | 'legacy_additional_winnings'> {
  /** Competition rank by total: equal totals share a place, the next skips. */
  place: number;
  /** Under an event type filter: that type's external results only (the legacy lump has no event). */
  additional_winnings: number;
}

/** An event type with scored results in the season — the filter's options. */
export interface SeasonEventType {
  id: string;
  name: string;
}

export interface SeasonStandingsResponse {
  organization_id: string;
  organization_name: string | null;
  short_name: string | null;
  season_year: number;
  rodeo_count: number;
  /** The filter applied; null = every event type. */
  event_type_id: string | null;
  event_types: SeasonEventType[];
  /** Sorted by total_winnings desc, then display_name. */
  contestants: SeasonStandingsRow[];
}

/** One contestant's ledger behind a board row — fetched only on expand. */
export interface SeasonStandingsContestantResponse {
  user_profile_id: string;
  display_name: string | null;
  /** Under an event type filter: that type's external results only. */
  additional_winnings: number;
  breakdown: SeasonBreakdownRow[];
}

/** One association on the standings picker — every real one, most active first. */
export interface SeasonAssociation {
  organization_id: string;
  organization_name: string | null;
  short_name: string | null;
  /** Seasons with non-cancelled rodeos, newest first. Never empty. */
  years: number[];
  /** Non-cancelled rodeos in the newest season. */
  rodeo_count: number;
}

// --- Query Key Factory ---
export const seasonWinningsKeys = {
  all: ['season-winnings'] as const,
  /** Everything under one association+season, so one write invalidates it all. */
  season: (orgId: string, year: number) =>
    [...seasonWinningsKeys.all, orgId, year] as const,
  roster: (orgId: string, year: number) =>
    [...seasonWinningsKeys.season(orgId, year), 'roster'] as const,
  contestant: (orgId: string, year: number, userId: string) =>
    [...seasonWinningsKeys.season(orgId, year), 'contestant', userId] as const,
  mine: (orgId?: string, year?: number) =>
    [...seasonWinningsKeys.all, 'mine', orgId, year] as const,
  myOrganizations: () => [...seasonWinningsKeys.all, 'my-organizations'] as const,
  /** Under season(), so a delegate's adjustment write refreshes the board too. */
  standings: (orgId: string, year: number, eventTypeId: string | null) =>
    [...seasonWinningsKeys.season(orgId, year), 'standings', eventTypeId] as const,
  standingsContestant: (
    orgId: string,
    year: number,
    userId: string,
    eventTypeId: string | null,
  ) =>
    [...seasonWinningsKeys.season(orgId, year), 'standings-contestant', userId, eventTypeId] as const,
  associations: () => [...seasonWinningsKeys.all, 'associations'] as const,
};

/** The server recomputes standings for the whole season on a miss. */
const SEASON_STALE_MS = 60_000;

// --- Query Hooks ---

export function useSeasonRoster(orgId?: string, year?: number) {
  return useQuery({
    queryKey: seasonWinningsKeys.roster(orgId ?? '', year ?? 0),
    enabled: !!orgId && !!year,
    staleTime: SEASON_STALE_MS,
    queryFn: () =>
      httpClient.get<SeasonRosterResponse>(
        `/admin-ops/organizations/${orgId}/season/${year}/roster`,
      ),
  });
}

export function useContestantSeason(orgId?: string, year?: number, userId?: string) {
  return useQuery({
    queryKey: seasonWinningsKeys.contestant(orgId ?? '', year ?? 0, userId ?? ''),
    enabled: !!orgId && !!year && !!userId,
    staleTime: SEASON_STALE_MS,
    queryFn: () =>
      httpClient.get<ContestantSeasonResponse>(
        `/admin-ops/organizations/${orgId}/season/${year}/contestants/${userId}`,
      ),
  });
}

/**
 * The associations to offer in the season picker. Comes from the season service
 * rather than the memberships endpoint, so an association the contestant only
 * ever competed at is on the list — and so every entry on the list is one
 * `useMySeasonWinnings` will actually answer for, never a 403.
 */
export function useMySeasonOrganizations() {
  return useQuery({
    queryKey: seasonWinningsKeys.myOrganizations(),
    staleTime: SEASON_STALE_MS,
    queryFn: () =>
      httpClient.get<SeasonOrganization[]>('/dashboard-data/my/season-organizations'),
  });
}

export function useMySeasonWinnings(orgId?: string, year?: number) {
  return useQuery({
    queryKey: seasonWinningsKeys.mine(orgId, year),
    enabled: !!orgId,
    staleTime: SEASON_STALE_MS,
    queryFn: () => {
      const params = new URLSearchParams({ organization_id: orgId! });
      if (year) params.set('year', String(year));
      return httpClient.get<MySeasonWinningsResponse>(
        `/dashboard-data/my/season-winnings?${params.toString()}`,
      );
    },
  });
}

/** Every real association, most active first — the standings board's picker. */
export function useSeasonAssociations() {
  return useQuery({
    queryKey: seasonWinningsKeys.associations(),
    staleTime: SEASON_STALE_MS,
    queryFn: () =>
      httpClient.get<SeasonAssociation[]>('/dashboard-data/season-associations'),
  });
}

/**
 * The association standings board: every contestant's running total, any org.
 * `eventTypeId` re-totals the numbers a type owns server-side; null = all.
 */
export function useSeasonStandings(orgId?: string, year?: number, eventTypeId: string | null = null) {
  return useQuery({
    queryKey: seasonWinningsKeys.standings(orgId ?? '', year ?? 0, eventTypeId),
    enabled: !!orgId && !!year,
    staleTime: SEASON_STALE_MS,
    // A filter change re-totals the same board; keep the rows on screen while
    // the new figures arrive rather than dropping to a skeleton.
    placeholderData: keepPreviousData,
    queryFn: () => {
      const params = new URLSearchParams({
        organization_id: orgId!,
        year: String(year),
      });
      if (eventTypeId) params.set('event_type_id', eventTypeId);
      return httpClient.get<SeasonStandingsResponse>(
        `/dashboard-data/season-standings?${params.toString()}`,
      );
    },
  });
}

/**
 * One board row's result ledger. Disabled until the row is expanded, so the
 * board load never carries a breakdown; cached per (season, contestant, filter).
 */
export function useSeasonStandingsContestant(
  orgId: string | undefined,
  year: number | undefined,
  userId: string | undefined,
  eventTypeId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: seasonWinningsKeys.standingsContestant(orgId ?? '', year ?? 0, userId ?? '', eventTypeId),
    enabled: enabled && !!orgId && !!year && !!userId,
    staleTime: SEASON_STALE_MS,
    queryFn: () => {
      const params = new URLSearchParams({
        organization_id: orgId!,
        year: String(year),
        user_profile_id: userId!,
      });
      if (eventTypeId) params.set('event_type_id', eventTypeId);
      return httpClient.get<SeasonStandingsContestantResponse>(
        `/dashboard-data/season-standings/contestant?${params.toString()}`,
      );
    },
  });
}

// --- Mutation Hooks ---

/**
 * Save a contestant's delegate-entered winnings / finals eligibility.
 * Invalidates the whole association+season subtree: the roster's totals and
 * the contestant's detail are one view of the same numbers.
 *
 * The invalidation is awaited, so a caller's per-mutate `onSuccess` /
 * `onSettled` runs once the refetched season is on screen — an optimistic
 * badge can drop its override then without flashing the old value first.
 * Cheap to await: the server layers adjustments onto its cached season, so
 * the refetch never triggers a recompute.
 */
export function useUpsertSeasonAdjustment(orgId: string, year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      ...data
    }: UpsertSeasonAdjustmentInput & { userId: string }) =>
      httpClient.put<{ success: true; adjustment: SeasonAdjustment }>(
        `/admin-ops/organizations/${orgId}/season/${year}/contestants/${userId}/adjustment`,
        data,
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: seasonWinningsKeys.season(orgId, year) }),
        // The contestant's own card reads the same adjustment.
        qc.invalidateQueries({ queryKey: [...seasonWinningsKeys.all, 'mine'] }),
      ]),
  });
}

/**
 * Newest event first, then most recently entered — the order the server
 * lists `external_results` in, so a row patched in locally sits where the
 * refetch will put it.
 */
function sortExternalResults(rows: SeasonExternalResult[]): SeasonExternalResult[] {
  return [...rows].sort(
    (a, b) =>
      b.event_date.localeCompare(a.event_date) ||
      (b.created_at ?? '').localeCompare(a.created_at ?? ''),
  );
}

/**
 * Patch the contestant detail already on screen after an external-result
 * write, then invalidate the season subtree WITHOUT waiting for it.
 *
 * The write itself is a single-row insert / update / delete; what used to
 * make "Add" spin for seconds was awaiting the roster + standings refetch
 * behind it. So the list is updated from the returned row (or by dropping the
 * deleted id) the moment the write completes, `isPending` clears, and the
 * stat tiles and roster catch up when their refetch lands a moment later.
 */
function useApplyExternalResultWrite(orgId: string, year: number) {
  const qc = useQueryClient();
  return (
    userId: string,
    patch: (rows: SeasonExternalResult[]) => SeasonExternalResult[],
  ): void => {
    qc.setQueryData<ContestantSeasonResponse>(
      seasonWinningsKeys.contestant(orgId, year, userId),
      (current) =>
        current
          ? { ...current, external_results: sortExternalResults(patch(current.external_results)) }
          : current,
    );
    // Fire-and-forget on purpose — see the note above.
    void qc.invalidateQueries({ queryKey: seasonWinningsKeys.season(orgId, year) });
    void qc.invalidateQueries({ queryKey: [...seasonWinningsKeys.all, 'mine'] });
  };
}

const externalResultsPath = (orgId: string, year: number, userId: string) =>
  `/admin-ops/organizations/${orgId}/season/${year}/contestants/${userId}/external-results`;

/** Record one result from a rodeo HighCall did not run. */
export function useCreateExternalResult(orgId: string, year: number) {
  const apply = useApplyExternalResultWrite(orgId, year);
  return useMutation({
    mutationFn: ({ userId, ...data }: ExternalResultInput & { userId: string }) =>
      httpClient.post<{ success: true; external_result: SeasonExternalResult }>(
        externalResultsPath(orgId, year, userId),
        data,
      ),
    onSuccess: (response, { userId }) =>
      apply(userId, (rows) => [
        response.external_result,
        ...rows.filter((r) => r.id !== response.external_result.id),
      ]),
  });
}

/** Patch one external result; omitted fields are left alone. */
export function useUpdateExternalResult(orgId: string, year: number) {
  const apply = useApplyExternalResultWrite(orgId, year);
  return useMutation({
    mutationFn: ({
      userId,
      id,
      ...data
    }: Partial<ExternalResultInput> & { userId: string; id: string }) =>
      httpClient.put<{ success: true; external_result: SeasonExternalResult }>(
        `${externalResultsPath(orgId, year, userId)}/${id}`,
        data,
      ),
    onSuccess: (response, { userId }) =>
      apply(userId, (rows) =>
        rows.map((r) => (r.id === response.external_result.id ? response.external_result : r)),
      ),
  });
}

export function useDeleteExternalResult(orgId: string, year: number) {
  const apply = useApplyExternalResultWrite(orgId, year);
  return useMutation({
    mutationFn: ({ userId, id }: { userId: string; id: string }) =>
      httpClient.delete<{ success: true }>(`${externalResultsPath(orgId, year, userId)}/${id}`),
    onSuccess: (_response, { userId, id }) =>
      apply(userId, (rows) => rows.filter((r) => r.id !== id)),
  });
}
